import { forwardRef, useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { useIntl } from "react-intl";
import { airportcity, updateairlines } from "../../../utils";
import { selectSearch, setSearchOption } from "../../../reducers/searchSlice";
import { getFilters } from "../../../reducers/api";
import "./AirportDropdown.scss";
import "../Dropdown.scss";
import Button from "../../ui/Button/Button";

const AirportDropdown = forwardRef(
  ({ onSelect, searchValue, onClose, selectedValue = [] }, ref) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const { stations } = useSelector(selectSearch);
    const [expandedRegions, setExpandedRegions] = useState({});
    const [localSelectedAirports, setLocalSelectedAirports] = useState([]);

    const airlines = useSelector((state) => state.properties.airlines);
    const nolowcost = useSelector((state) => state.search.nolowcost);
    const linie = useSelector((state) => state.search.linie);
    const airlinerank = useSelector((state) => state.search.airlinerank);
    const departureStations = useSelector(
      (state) => state.properties.departureStations
    );

    useEffect(() => {
      if (Array.isArray(selectedValue)) {
        setLocalSelectedAirports(selectedValue);
      } else if (stations.length > 0 && selectedValue.length === 0) {
        setLocalSelectedAirports(stations);
      }
    }, [selectedValue, stations]);

    const handleSelectAll = useCallback(
      (airportsData) => {
        let currentStations = [...localSelectedAirports];
        let tmpstations = currentStations.map((item) => item.value);
        let tmpdata = airportsData.map((item) => item.value);

        if (tmpdata.every((o) => tmpstations.includes(o))) {
          tmpstations = tmpstations.filter((o) => !tmpdata.includes(o));
        } else {
          tmpstations = [...tmpstations, ...tmpdata];
        }

        let uniqueairports = [...new Set(tmpstations)];
        let val = [];
        for (let i = 0; i < uniqueairports.length; i++) {
          val.push({
            value: uniqueairports[i],
            label: airportcity(uniqueairports[i]),
          });
        }

        setLocalSelectedAirports(val);
      },
      [localSelectedAirports]
    );

    const groupDepartureStations = useCallback(() => {
      let tmp = [];
      if (Array.isArray(departureStations)) {
        tmp = [...departureStations];
      }

      if (Array.isArray(tmp)) {
        let tmpall = { 
          label: intl.formatMessage({ id: 'allAirports' }), 
          options: [] 
        };

        for (let i = 0; i < tmp.length; i++) {
          if (tmp[i].airports) {
            let tmpoption = {
              label: tmp[i].label,
              airports: tmp[i].airports,
              options: [],
            };

            for (let j = 0; j < tmp[i].airports.length; j++) {
              tmpoption.options.push({
                value: tmp[i].airports[j],
                label: airportcity(tmp[i].airports[j]),
              });
            }
            tmp[i] = tmpoption;
          } else {
            tmpall.options.push(tmp[i]);
          }
        }
        tmp.push(tmpall);
      }

      return tmp.filter((o) => o.options && o.options.length > 0);
    }, [departureStations, intl]);

    const handleRegionToggle = (regionLabel) => {
      setExpandedRegions((prev) => ({
        ...prev,
        [regionLabel]: !prev[regionLabel],
      }));
    };

    const handleAirportSelect = (airport) => {
      const existingIndex = localSelectedAirports.findIndex(
        (item) => item.value === airport.value
      );

      let newSelection;
      if (existingIndex >= 0) {
        newSelection = localSelectedAirports.filter(
          (_, index) => index !== existingIndex
        );
      } else {
        newSelection = [...localSelectedAirports, airport];
      }

      setLocalSelectedAirports(newSelection);
    };

    const handleRegionSelect = (region) => {
      handleSelectAll(region.options);
    };

    // Normalizer for searching (umlauts/diacritics)
    const normalize = (s = "") =>
      s
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/ä/g, "a")
        .replace(/ö/g, "o")
        .replace(/ü/g, "u")
        .replace(/ß/g, "ss");

    const getFilteredAirports = useCallback(() => {
      if (!searchValue || searchValue.length < 2) return null;

      const q = normalize(searchValue);
      const groupedStations = groupDepartureStations();
      let allAirports = [];

      groupedStations.forEach((group) => {
        if (group.options) {
          allAirports.push(...group.options);
        }
      });

      const filtered = allAirports.filter(
        (a) => normalize(a.value).includes(q) || normalize(a.label).includes(q)
      );

      const uniqueAirports = filtered.reduce((acc, current) => {
        const isDuplicate = acc.find((item) => item.value === current.value);
        if (!isDuplicate) {
          acc.push(current);
        }
        return acc;
      }, []);

      return uniqueAirports;
    }, [searchValue, groupDepartureStations]);

    const isAirportSelected = (airportValue) => {
      return localSelectedAirports.some((item) => item.value === airportValue);
    };

    const isRegionFullySelected = (region) => {
      if (!region.options || region.options.length === 0) return false;
      return region.options.every((airport) =>
        isAirportSelected(airport.value)
      );
    };

    const isRegionPartiallySelected = (region) => {
      if (!region.options || region.options.length === 0) return false;
      return (
        region.options.some((airport) => isAirportSelected(airport.value)) &&
        !isRegionFullySelected(region)
      );
    };

    const handleSubmit = async () => {
      dispatch(
        setSearchOption({ property: "stations", data: localSelectedAirports })
      );

      try {
        const originalPromiseResult = await getFilters(dispatch, false).then(
          unwrapResult
        );
        updateairlines(
          dispatch,
          airlines,
          originalPromiseResult,
          airlinerank,
          nolowcost,
          linie
        );
      } catch (error) {
        console.error("Error updating filters:", error);
      }

      if (onSelect) {
        onSelect(localSelectedAirports);
      }

      if (onClose) {
        onClose();
      }
    };

    const groupedStations = groupDepartureStations();
    const filtered = getFilteredAirports();

    return (
      <div className="main_input-dropdown airport-dropdown" ref={ref}>
        <div className="dropdown-header">
          <span className="dropdown-title">
            {intl.formatMessage({ id: 'allDepartureAirports' })}
          </span>
          <span className="dropdown-subtitle">
            {intl.formatMessage({ id: 'multipleSelectionPossible' })}
          </span>
        </div>

        <div className="airport-content-container">
          <div className="airport-section">
            {filtered !== null ? (
              <div className="airport-list">
                {filtered.length ? (
                  filtered.map((airport, index) => (
                    <div
                      key={`${airport.value}-${index}`}
                      className={`airport-option ${
                        isAirportSelected(airport.value) ? "selected" : ""
                      }`}
                      onClick={() => handleAirportSelect(airport)}
                    >
                      <div className="airport-checkbox">
                        <input
                          type="checkbox"
                          checked={isAirportSelected(airport.value)}
                          readOnly
                        />
                      </div>
                      <div className="airport-info">
                        <span className="airport-name">{airport.label}</span>
                        <span className="airport-code">{airport.value}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-results">
                    {intl.formatMessage({ id: 'nothingFound' })}
                  </div>
                )}
              </div>
            ) : (
              groupedStations.map((region, regionIndex) => (
                <div
                  key={`${region.label}-${regionIndex}`}
                  className="airport-region"
                >
                  <div
                    className={`region-header ${
                      expandedRegions[region.label] ? "expanded" : ""
                    }`}
                    onClick={() => handleRegionToggle(region.label)}
                  >
                    <div
                      className="region-checkbox"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isRegionFullySelected(region)}
                        ref={(input) => {
                          if (input)
                            input.indeterminate =
                              isRegionPartiallySelected(region);
                        }}
                        onChange={() => handleRegionSelect(region)}
                      />
                    </div>
                    <div className="region-content">
                      <span className="region-name">{region.label}</span>
                      <span className="region-subtitle">
                        {region.options
                          ? `${region.options.length} ${intl.formatMessage({ id: 'airports' })}`
                          : ""}
                      </span>
                    </div>
                    <div className="region-arrow">
                      {expandedRegions[region.label] ? "▲" : "▼"}
                    </div>
                  </div>

                  {expandedRegions[region.label] && region.options && (
                    <div className="airport-list">
                      {region.options.map((airport, airportIndex) => (
                        <div
                          key={`${airport.value}-${airportIndex}`}
                          className={`airport-option ${
                            isAirportSelected(airport.value) ? "selected" : ""
                          }`}
                          onClick={() => handleAirportSelect(airport)}
                        >
                          <div className="airport-checkbox">
                            <input
                              type="checkbox"
                              checked={isAirportSelected(airport.value)}
                              readOnly
                            />
                          </div>
                          <div className="airport-info">
                            <span className="airport-name">
                              {airport.label}
                            </span>
                            <span className="airport-code">
                              {airport.value}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="dropdown-footer">
          {localSelectedAirports.length > 0 && (
            <div className="selected-count">
              {localSelectedAirports.length} {intl.formatMessage({ id: 'airportsSelected' })}
            </div>
          )}

          <Button
            text={intl.formatMessage({ id: 'apply' })}
            style={"rounded"}
            color={"orange"}
            size={"m"}
            onClick={handleSubmit}
          />
        </div>
      </div>
    );
  }
);

export default AirportDropdown;